import { FC, MouseEventHandler, useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { Chip, IconButton, Typography, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import SearchIcon from "@mui/icons-material/Search";

import { FindInstruments } from "@/models/find-instruments";
import { backgroundColorState, blackLighterState, greyDarkerState, greyLighterState, newbackgroundColorState, searchSettingsState, themeState, userState, whiteDarkerState } from "@/lib/store";
import { classNames } from "@/lib/utils";
import { Settings } from "@/models/auth";
import { updateUserSettings } from "@/controllers/auth";
import { usExchanges } from "@/lib/vars";
import { getPreferredTabSuffix, readPreferredStockTab } from "@/lib/stock-tabs";

import styles from "./HeaderAutocomplete.module.scss";
import ImageNotSupported from "@mui/icons-material/ImageNotSupported";
import { getMissingLogo } from "@/controllers/stock";

import { useNoBackgroundLogos } from "src/hooks/useNoBackgroundLogos";
import { useCompanyLogo, useLogosConfig } from "@/lib/logos";

interface PropsTypes {
    activeIndex?: number;
    instruments: FindInstruments[];
    onClick?: (instrument: FindInstruments) => void;
    onMouseEnter?: MouseEventHandler<HTMLDivElement>;
    changeStockModule?: boolean;
    setNewStock?: (stock) => void;
}

let timeout: NodeJS.Timeout;

/**
 * One search result's logo. A component of its own because `useCompanyLogo` is a hook and the
 * results are rendered in a `.map()`: it asks the service for artwork that reads on the page
 * background, and draws a white plate when the service answers that none exists.
 */
const InstrumentLogo = ({ instrument, theme, plated = true, onMissing }: { instrument: any; theme: string; plated?: boolean; onMissing: () => void }) => {
    const logosCfg = useLogosConfig();
    const logo = useCompanyLogo(instrument, "rect", { onMissing });
    const legacyPlate = logosCfg.source === "edge" ? "transparent" : theme === "dark" && plated ? "#c0c0c0" : "transparent";
    if (logo.failed) return <ImageNotSupported />;
    return (
        <div className={styles.logo} style={{ width: "80px", backgroundColor: logo.plate ?? legacyPlate, borderRadius: "4px" }}>
            <Image unoptimized src={logo.src} alt={`${instrument.name} Logo`} layout="fill" objectFit="contain" priority onError={logo.onError} />
        </div>
    );
};

const HeaderAutocomplete: FC<PropsTypes> = ({ activeIndex, instruments, onClick, changeStockModule, setNewStock, onMouseEnter }) => {
    const logosCfg = useLogosConfig(); // re-render when web flips the logo source or rotates the token
    const { logoNoBackground } = useNoBackgroundLogos();
    const theme = useRecoilValue(themeState);
    const greyDarker = useRecoilValue(greyDarkerState);
    const blackLighter = useRecoilValue(blackLighterState);
    const whiteDarker = useRecoilValue(whiteDarkerState);
    const greyLighter = useRecoilValue(greyLighterState);
    const backgroundColor = useRecoilValue(backgroundColorState);
    const newbackgroundColor = useRecoilValue(newbackgroundColorState);
    const textColor = useRecoilValue(greyLighterState);
    const [user, setUser] = useRecoilState(userState);
    const searchSettings = useRecoilValue(searchSettingsState);
    const [activeItem, setActiveItem] = useState(undefined);

    const [instrumentsLocal, setInstrumentsLocal] = useState<FindInstruments[]>([]);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        if (activeIndex !== null && activeIndex !== undefined && itemRefs.current[activeIndex]) {
            itemRefs.current[activeIndex].scrollIntoView({ block: "nearest" });
        }
    }, [activeIndex]);


    const isMobile = useMediaQuery("(max-width: 900px)");

    const searchLinks = [
        {
            text: "overview",
            link: "",
        },
        {
            text: "shorts",
            link: "short-interest",
        },
        {
            text: "analysts",
            link: "analysts",
        },
        {
            text: "options",
            link: "options",
        },
        {
            text: "signals",
            link: "trading-signals",
        },
        {
            text: "scores",
            link: "stock-scores",
        },
    ];

    const etfSearchLinks = [
        {
            text: "overview",
            link: "",
        },
        {
            text: "corp actions",
            link: "corporate-actions",
        },
        {
            text: "options",
            link: "options",
        },
        {
            text: "shorts",
            link: "short-interest",
        },
        {
            text: "signals",
            link: "trading-signals",
        },
    ];

    const nonUSExchange = (exchange: string) => !usExchanges.includes(exchange);

    const getStockLinks = (instrument: FindInstruments) => {
        const links = instrument.security_type === "ETF" ? etfSearchLinks : searchLinks;

        if (nonUSExchange(instrument.exchange)) {
            return links.filter((link) => link.text !== "options").map((link, index) => searchLinkItem(instrument, link.text, link.link, index));
        } else {
            return links.map((link, index) => searchLinkItem(instrument, link.text, link.link, index));
        }
    };

    const handleSearchClick = (instrument: FindInstruments): void => {
        const searchArray = [...searchSettings, instrument];
        const search_settings = searchArray.filter((value, index, self) => index === self.findIndex((item) => item.name === value.name));
        if (search_settings.length > 5) {
            search_settings.splice(0, 1);
        }
        const settings: Settings = { ...user.settings, search_settings };
        setUser({ ...user, settings });

        if (process.env.NEXT_PUBLIC_VERCEL_ENV && user.subscription_plan !== "ORTEX Visitor") {
            clearTimeout(timeout);
            timeout = setTimeout(() => updateUserSettings(settings), 1000);
        }
    };

    const searchLinkItem = (instrument: FindInstruments, text: string, link: string, index: number) => {
        return (
            <Link key={index} href={`/s/${instrument.exchange}/${instrument.ticker}/${link}`} passHref>
                <a style={{ color: textColor }} onClick={() => handleSearchClick(instrument)}>
                    {text.toUpperCase()}
                </a>
            </Link>
        );
    };

    useEffect(() => {
        setInstrumentsLocal(instruments);

    }, [instruments]);

    const reportMissingLogo = (security_id: number) => {
        if (security_id) getMissingLogo(security_id);
    };

    return (
        <div className={styles.HeaderAutocomplete} style={{ display: instrumentsLocal.length > 0 ? "block" : "none", top: isMobile ? "calc(100px + env(safe-area-inset-top) + 26px)" : "85px" }} onMouseEnter={onMouseEnter}>
            <style jsx>{`
                .${styles.instrument}:hover, .${styles.instrument}.active {
                    background-color: ${theme === "dark" ? blackLighter : whiteDarker} !important;
                }

                .${styles.instrumentsContainer}::-webkit-scrollbar {
                    background-color: ${backgroundColor};
                }
            `}</style>
            <div className={styles.instrumentsContainer}>
                {instrumentsLocal.map((instrument, index) => (
                    <div
                        key={instrument.value}
                        ref={(el) => (itemRefs.current[index] = el)}
                        className={classNames(styles.instrument, index === activeIndex && "active", activeItem && activeItem === instrument && styles.isOpen)}
                        style={{ borderBottom: `1px solid ${theme === "dark" ? "greyDarker" : whiteDarker}`, backgroundColor: theme === "dark" ? backgroundColor : newbackgroundColor }}
                        onClick={onClick ? () => onClick(instrument) : () => setActiveItem(instrument)}
                    >
                        <div className={styles.instrumentWrapper}>
                            {instrument.is_history ? <HistoryIcon htmlColor={greyLighter} fontSize="medium" sx={{ mr: isMobile ? 1 : 3 }} /> : <SearchIcon htmlColor={greyLighter} fontSize="medium" sx={{ mr: isMobile ? 1 : 3 }} />}
                            <div className={styles.logo}>
                                <InstrumentLogo
                                    instrument={instrument}
                                    theme={theme}
                                    plated={!logoNoBackground(instrument.security_id)}
                                    onMissing={() => reportMissingLogo(instrument.security_id)}
                                />
                            </div>
                            <div className={styles.instrumentContent}>
                                <div className={styles.instrumentInfo}>
                                    <Typography sx={{ fontWeight: 700 }} className={styles.ticker}>
                                        {instrument.exchange}:{instrument.ticker}
                                    </Typography>
                                    <Typography variant="body2" className={styles.name}>
                                        {instrument.name}
                                    </Typography>
                                    <div className={styles.type}>
                                        <Chip className={styles.securityType} label={instrument.security_type} color={instrument.security_type === "ETF" ? "secondary" : "info"} variant="outlined" size="small" />
                                    </div>
                                </div>
                                <div className={styles.instrumentLinks}>{getStockLinks(instrument)}</div>
                                <div className={styles.instrumentMobileLinks} style={{ backgroundColor }}>
                                    <div className={styles.instrumentMobileLinksWrapper}>{getStockLinks(instrument)}</div>
                                    <IconButton
                                        aria-label="close"
                                        style={{ color: textColor }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setActiveItem(undefined);
                                        }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </div>
                            </div>
                        </div>
                        {!changeStockModule && !onClick && (
                            <Link href={`/s/${instrument.exchange || " ‎"}/${instrument.ticker}${getPreferredTabSuffix(readPreferredStockTab(user?.settings), instrument.security_type, instrument.exchange)}`}>
                                <a onClick={() => handleSearchClick(instrument)}></a>
                            </Link>
                        )}
                        {changeStockModule && (
                            <div className={styles.changeStockModule}>
                                <button
                                    className={styles.changeStockModuleButton}
                                    onClick={() => {
                                        setNewStock(instrument);
                                    }}
                                >
                                    Change
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default HeaderAutocomplete;
